import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLatest } from './useLatest'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useBruktbilFilters } from './useBruktbilFilters'
import {
  MAX_SAMMENLIGNING_ORGS,
  fetchOrgOptions,
  fetchOrgSammenligning,
} from './sammenligningApi'
import type { OrgOption, OrgSammenligningResponse, OrgSide } from './sammenligningApi'
import { formatAar, formatKjoperKlasser } from './kjoperKlasser'

const DEBOUNCE_MS = 250

// Same fixed palette as the Merke & modell trend chart — cycled by series
// index so a line keeps its colour while others are added or removed.
const PALETTE = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
]

/**
 * Compare up to 10 organisations' monthly volume — Selgere (#484) and Kjøpere
 * (#488) share this component. Orgs are picked with a server-side typeahead
 * (name contains / org_nr prefix) rather than from the ranked list, so an org
 * outside the top page can still be compared. The period is the module's
 * shared date filter above the tabs.
 */
export default function OrgSammenligningTab({ side }: { side: OrgSide }) {
  const { from, to, klasse, varebil, omregistrering, selgerPostnummer, kjoperPostnummer } = useBruktbilFilters()
  const { t: tr } = useTranslation('bruktbil')
  const max = MAX_SAMMENLIGNING_ORGS
  // Testids keep the plural tab names the specs already use (selgere-*/kjopere-*).
  const tid = side === 'selger' ? 'selgere' : 'kjopere'
  const params = { from, to, klasse, varebil, omregistrering, selgerPostnummer, kjoperPostnummer }

  const [valgte, setValgte] = useState<OrgOption[]>([])
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<OrgOption[]>([])
  const [data, setData] = useState<OrgSammenligningResponse | null>(null)
  const [loading, setLoading] = useState(false)

  const capReached = valgte.length >= MAX_SAMMENLIGNING_ORGS

  // Switching tab (selger ↔ kjøper) must not carry the other side's picks.
  useEffect(() => {
    setValgte([])
    setData(null)
    setQuery('')
  }, [side])

  useLatest(alive => {
    if (!query.trim()) {
      setOptions([])
      return
    }
    const t = setTimeout(() => {
      fetchOrgOptions(side, params, query.trim())
        .then(o => { if (alive()) setOptions(o) })
        .catch(() => { if (alive()) setOptions([]) })
    }, DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [side, query, from, to, JSON.stringify(klasse), varebil, omregistrering, selgerPostnummer, kjoperPostnummer])

  useLatest(alive => {
    if (valgte.length === 0) {
      setData(null)
      return
    }
    setLoading(true)
    fetchOrgSammenligning(side, params, valgte.map(v => v.orgNr))
      .then(r => { if (alive()) setData(r.data) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [side, JSON.stringify(valgte.map(v => v.orgNr)), from, to, JSON.stringify(klasse), varebil, omregistrering, selgerPostnummer, kjoperPostnummer])

  function add(o: OrgOption) {
    if (capReached || valgte.some(v => v.orgNr === o.orgNr)) return
    setValgte(prev => [...prev, o])
    setQuery('')
    setOpen(false)
  }

  function remove(orgNr: string) {
    setValgte(prev => prev.filter(v => v.orgNr !== orgNr))
  }

  // Series label: the name when known, else the org number — org_nr is what
  // keeps two organisations with the same name apart, so it is always shown.
  const label = (o: OrgOption) => (o.navn ? `${o.navn} (${o.orgNr})` : o.orgNr)
  const serieLabel = (orgNr: string, navn: string | null) =>
    label(valgte.find(v => v.orgNr === orgNr) ?? { orgNr, navn })

  // Pivot { maned, [label]: antall } for recharts.
  const chartData = (data?.maneder ?? []).map((maned, i) => {
    const point: Record<string, number | string> = { maned }
    for (const serie of data?.serier ?? []) {
      point[serieLabel(serie.org_nr, serie.org_navn)] = serie.trend[i]?.antall ?? 0
    }
    return point
  })

  return (
    <div className="space-y-3" data-testid={`${tid}-sammenligning`}>
      <div className="card p-3 space-y-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {tr(`orgSammenligning.${side}.intro`, { max })}
        </p>

        {valgte.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {valgte.map(v => (
              <span
                key={v.orgNr}
                className="inline-flex items-center gap-1 rounded-sm bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs px-2 py-1"
              >
                {label(v)}
                <button
                  type="button"
                  aria-label={tr('orgSammenligning.fjern', { name: v.navn ?? v.orgNr })}
                  onClick={() => remove(v.orgNr)}
                  className="text-primary-500 hover:text-primary-700"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="relative">
          <input
            type="text"
            data-testid={`${tid}-typeahead`}
            disabled={capReached}
            value={query}
            onFocus={() => setOpen(true)}
            onChange={e => { setQuery(e.target.value); setOpen(true) }}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder={capReached ? tr(`orgSammenligning.${side}.maksValgt`, { max }) : tr(`orgSammenligning.${side}.sok`)}
            className="w-full text-sm rounded-sm border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {open && !capReached && options.length > 0 && (
            <ul
              data-testid={`${tid}-typeahead-options`}
              className="absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded-sm border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-sm"
            >
              {options
                .filter(o => !valgte.some(v => v.orgNr === o.orgNr))
                .map(o => (
                  <li key={o.orgNr}>
                    <button
                      type="button"
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => add(o)}
                      className="block w-full text-left text-sm px-2 py-1 hover:bg-gray-50 dark:hover:bg-dark-border"
                    >
                      {o.navn ?? '—'}{' '}
                      <span className="font-mono text-xs text-gray-400">{o.orgNr}</span>
                    </button>
                  </li>
                ))}
            </ul>
          )}
        </div>

        {capReached && (
          <p data-testid={`${tid}-cap-hint`} className="text-xs text-amber-600 dark:text-amber-400">
            {tr(`orgSammenligning.${side}.capHint`, { max })}
          </p>
        )}
      </div>

      {valgte.length === 0 && (
        <div className="card p-4 text-sm text-gray-500 dark:text-gray-400">
          {tr(`orgSammenligning.${side}.velgMinst`)}
        </div>
      )}

      {loading && valgte.length > 0 && (
        <div className="card p-4 animate-pulse h-72 bg-gray-100 dark:bg-dark-border rounded-sm" />
      )}

      {!loading && data && valgte.length > 0 && (
        <div className="card p-4" data-testid={`${tid}-sammenligning-chart`}>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{tr(`orgSammenligning.${side}.chart`)}</h3>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="maned" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip formatter={(v: number) => v.toLocaleString('nb-NO')} />
              <Legend />
              {data.serier.map((s, i) => (
                <Line
                  key={s.org_nr}
                  type="monotone"
                  dataKey={serieLabel(s.org_nr, s.org_navn)}
                  stroke={PALETTE[i % PALETTE.length]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>

          <table className="data-table data-table-compact mt-4" data-testid={`${tid}-sammenligning-tabell`}>
            <thead>
              <tr>
                <th className="text-left">{tr(`orgSammenligning.${side}.tabell`)}</th>
                <th className="text-left">{tr('orgSammenligning.orgNr')}</th>
                <th className="text-right whitespace-nowrap">{tr('orgSammenligning.totaltIPerioden')}</th>
                {/* The figures you actually compare organisations on (#594 AC5). */}
                <th className="text-right whitespace-nowrap">{tr('orgSammenligning.snittBilalder')}</th>
                <th className="text-right whitespace-nowrap">{tr('orgSammenligning.snittKjoperalder')}</th>
                <th className="text-left whitespace-nowrap">{tr('orgSammenligning.topp3Kjoperklasser')}</th>
              </tr>
            </thead>
            <tbody>
              {data.serier.map(s => (
                <tr key={s.org_nr}>
                  <td>{s.org_navn ?? valgte.find(v => v.orgNr === s.org_nr)?.navn ?? '—'}</td>
                  <td className="font-mono text-xs text-gray-500">{s.org_nr}</td>
                  <td className="text-right">{s.total.toLocaleString('nb-NO')}</td>
                  <td className="text-right whitespace-nowrap">{formatAar(s.avg_bilalder)}</td>
                  <td className="text-right whitespace-nowrap">{formatAar(s.avg_kjoperalder)}</td>
                  <td className="whitespace-nowrap">{formatKjoperKlasser(s.kjoper_klasser)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
